import { z } from 'zod';
/** Максимум событий в одной пачке. */
export const APP_EVENT_BATCH_LIMIT = 50;
/** Сколько дней держим сырые события, дальше чистим по расписанию. */
export const APP_EVENT_RETENTION_DAYS = 180;
/**
 * Имена событий — закрытый список, а не свободная строка.
 *
 * Свободная строка за пару месяцев превращается в expense_saved,
 * expenseSaved и expense-saved одновременно, и ни один запрос по ней больше
 * не сходится. Добавление имени сюда — осознанный шаг, который проходит
 * через ревью и типы на обоих концах.
 */
export const APP_EVENT_NAMES = [
    // Жизненный цикл
    'session_started',
    'screen_viewed',
    // Расход: полная воронка, включая брошенную форму.
    // Домашние таблицы знают только про сохранённые расходы — здесь видно,
    // сколько форм открыли и закрыли ни с чем.
    'expense_form_opened',
    'expense_form_abandoned',
    'expense_saved',
    'expense_field_used',
    // Что люди ищут в своих же данных: подсказывает, каких разрезов не хватает
    'expenses_filtered',
    // Видел ли человек перерасход
    'dashboard_viewed',
    'dashboard_customized',
    'budget_overrun_seen',
    // Прочие сущности
    'project_created',
    'stage_created',
    'stage_item_toggled',
    'material_created',
    // Еженедельный отчёт — единственный канал, которым продукт напоминает о себе
    // сам. Включение фиксируем вместе с местом (props.source), а показ и закрытие
    // подсказки — чтобы считать конверсию, а не только абсолютное число включений.
    'weekly_report_promo_shown',
    'weekly_report_promo_dismissed',
    'weekly_report_enabled',
    'weekly_report_disabled',
    'weekly_report_sent_manually',
    // Заходы на «Аналитику» и чтение отчёта отдельными именами не заводим:
    // первое видно по screen_viewed, второе — по weekly_report_logs.read_at.
    'support_request_sent',
    // Анонсы «что нового»: показ и закрытие — чтобы считать не только охват,
    // но и долю тех, кто нажал кнопку (props.cta).
    'announcement_shown',
    'announcement_closed',
    // Планировщик. Воронка «вход → план → стены → комната → слои», плюс
    // итог каждой сессии редактора одним событием (plan_session_ended): что
    // человек реально делал, а не только что открыл. Содержимое плана (имена
    // комнат, координаты) сюда не попадает — только счётчики и типы из каталога.
    'planner_entry_opened',
    'plan_created',
    'plan_opened',
    'plan_room_formed',
    'plan_opening_added',
    'plan_balcony_added',
    'plan_traced',
    'plan_item_added',
    'plan_auto_arranged',
    'plan_layer_switched',
    'plan_background_added',
    'plan_ai_traced',
    'plan_3d_opened',
    'plan_tour',
    'plan_deleted',
    'plan_session_ended',
    // Трение
    'api_error',
    'form_validation_failed',
];
/**
 * Значения props — только скаляры, строки не длиннее 64 символов.
 *
 * Ограничение по длине здесь не ради экономии места: оно физически не даёт
 * случайно утащить в аналитику пользовательский текст — описание расхода,
 * имя подрядчика, название проекта. В props должны попадать признаки
 * («есть теги», «категория выбрана»), а не содержимое.
 */
const appEventPropValue = z.union([
    z.string().max(64),
    z.number().finite(),
    z.boolean(),
    z.null(),
]);
export const appEventSchema = z.object({
    name: z.enum(APP_EVENT_NAMES),
    sessionId: z.string().min(8).max(64),
    screen: z.string().max(120).optional(),
    projectId: z.string().max(64).optional(),
    props: z
        .record(z.string().max(40), appEventPropValue)
        .refine((value) => Object.keys(value).length <= 20, {
        message: 'props: не больше 20 ключей',
    })
        .optional(),
    occurredAt: z.string().datetime(),
});
export const appEventBatchSchema = z.object({
    source: z.enum(['telegram', 'webapp', 'unknown']).optional(),
    appVersion: z.string().max(32).optional(),
    events: z.array(appEventSchema).min(1).max(APP_EVENT_BATCH_LIMIT),
});
/**
 * Приводит путь роутера к маршруту без идентификаторов:
 * /projects/cmk123/expenses/cmk456/edit → /projects/:id/expenses/:id/edit
 *
 * Без этого «экран» получается уникальным на каждый проект, и сгруппировать
 * просмотры невозможно.
 */
export function normalizeScreen(pathname) {
    if (!pathname)
        return '/';
    const normalized = pathname
        .split('/')
        .map((segment) => {
        if (!segment)
            return segment;
        // cuid/uuid/числовые идентификаторы
        if (/^c[a-z0-9]{20,}$/i.test(segment))
            return ':id';
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment))
            return ':id';
        if (/^\d+$/.test(segment))
            return ':id';
        return segment;
    })
        .join('/');
    const trimmed = normalized.length > 1 ? normalized.replace(/\/+$/, '') : normalized;
    return (trimmed || '/').slice(0, 120);
}
//# sourceMappingURL=app-event.js.map